/**
 * §7.4c Model Validation. Backtests the engine's unmitigated SEIRD against an
 * OBSERVED historical-outbreak series from the verifier-first corpus
 * (@biomax/plugin-epi-validation — real data, cited PMID that round-trips) and
 * reports how well it tracked reality:
 *
 *   - peak-TIMING error (weeks) and peak-normalised SHAPE RMSE — the metrics
 *     that are meaningful given the confirmed-undercount × mitigation gap;
 *   - a calibration / reliability diagram: a parametric ensemble (R0 spread)
 *     rescaled to the observed final size to ISOLATE temporal shape, scored
 *     with the engine's own calibrationCurve + CRPS (forecast.ts);
 *   - the magnitude error, explicitly caveated (model = true infections,
 *     unmitigated; observed = confirmed cases, post-intervention).
 *
 * This replaces "trust the model" with "here is how it tracked COVID-2020 in
 * Italy, and here is the source." No fabricated data.
 */

import { useMemo, useState } from 'react';
import { findPathogen } from '@biomax/plugin-pathogen-database';
import { findRegion } from '@biomax/plugin-region-database';
import {
  simulateSeird, continuousRankedProbabilityScore, calibrationCurve,
  type CalibrationPoint,
} from '@biomax/plugin-epidemiology';
import {
  listOutbreaks, scoreBacktest, VALIDATION_REFERENCES, type ReferenceOutbreak,
} from '@biomax/plugin-epi-validation';
import { Sparkline } from '../sparkline';

/** Unmitigated weekly model incidence aligned to the outbreak's weekly grid. */
function weeklyIncidence(o: ReferenceOutbreak, r0: number): number[] | null {
  const p = findPathogen(o.pathogenId);
  const r = findRegion(o.regionId);
  if (!p || !r) return null;
  const gamma = 1 / p.infectiousPeriodDays.value;
  const sigma = 1 / p.latentPeriodDays.value;
  const beta = r0 * gamma;
  const ifr = p.ifrByAgeBand[Math.floor(p.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;
  const weeks = o.series.length;
  const sim = simulateSeird({ N: r.population.total, I0: 100, beta, gamma, sigma, ifr, durationDays: weeks * 7, dtDays: 1 });
  const S = sim.compartments.S!;
  const daily: number[] = [];
  for (let i = 1; i < S.length; i++) daily.push(Math.max(0, S[i - 1]! - S[i]!));
  const wk: number[] = [];
  for (let w = 0; w < weeks; w++) { let s = 0; for (let d = 0; d < 7; d++) s += daily[w * 7 + d] ?? 0; wk.push(s); }
  return wk;
}

function normPeak(a: readonly number[]): number[] { const m = Math.max(...a, 1); return a.map(x => x / m); }
function sum(a: readonly number[]): number { return a.reduce((s, x) => s + x, 0); }
function quantile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return NaN;
  if (sorted.length === 1) return sorted[0]!;
  const pos = (sorted.length - 1) * q, lo = Math.floor(pos), hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (pos - lo);
}

export function ValidationBacktest() {
  const outbreaks = listOutbreaks();
  const [outbreakId, setOutbreakId] = useState<string>(outbreaks[0]?.id ?? '');
  const [members, setMembers] = useState<number>(30);
  const [r0Spread, setR0Spread] = useState<number>(30);

  const view = useMemo(() => {
    const o = outbreaks.find(x => x.id === outbreakId) ?? outbreaks[0];
    if (!o) return null;
    const p = findPathogen(o.pathogenId);
    const r = findRegion(o.regionId);
    if (!p || !r) return { o, catalogMiss: true as const };

    // Observed + central unmitigated model, both drop the seed week (0).
    const observed = o.series.map(s => s.newCases).slice(1);
    const centralWk = weeklyIncidence(o, p.R0.value);
    if (!centralWk) return { o, catalogMiss: true as const };
    const model = centralWk.slice(1);

    const raw = scoreBacktest(observed, model);
    const shape = scoreBacktest(normPeak(observed), normPeak(model));

    // Parametric ensemble (R0 ± spread), each member RESCALED to the observed
    // final size — this removes the (unknown) ascertainment × mitigation scale
    // so calibration tests TEMPORAL SHAPE, not magnitude.
    let seed = 271828;
    const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0x100000000; };
    const obsTot = sum(observed);
    const rescaled: number[][] = [];
    for (let i = 0; i < members; i++) {
      const r0 = Math.max(0.1, p.R0.value * (1 + ((rand() - 0.5) * 2 * r0Spread) / 100));
      const wk = weeklyIncidence(o, r0)?.slice(1);
      if (!wk) continue;
      const tot = sum(wk);
      const factor = tot > 0 ? obsTot / tot : 0;
      rescaled.push(wk.map(x => x * factor));
    }

    const n = observed.length;
    const samplesByWeek: number[][] = [];
    const q025: number[] = [], q975: number[] = [], median: number[] = [];
    for (let i = 0; i < n; i++) {
      const s = rescaled.map(m => m[i] ?? 0).sort((a, b) => a - b);
      samplesByWeek.push(s);
      q025.push(quantile(s, 0.025)); median.push(quantile(s, 0.5)); q975.push(quantile(s, 0.975));
    }
    const forecasts = observed.map((obs, i) => ({ samples: samplesByWeek[i]!, observed: obs }));
    const calibration: CalibrationPoint[] = calibrationCurve(forecasts);
    const crpsMean = forecasts.reduce((acc, f) => acc + continuousRankedProbabilityScore(f.samples, f.observed), 0) / Math.max(1, forecasts.length);
    const cov = (q: number) => calibration.find(c => c.predictedQuantile === q)?.empiricalCoverage ?? NaN;
    const cov80 = cov(0.9) - cov(0.1); // central-80% interval coverage (ideal 0.80)

    const ref = VALIDATION_REFERENCES.find(v => v.pmid === o.source.pmid);

    return {
      o, p, r, observed, model, raw, shape,
      obsNorm: normPeak(observed), modNorm: normPeak(model),
      q025, q975, median, calibration, crpsMean, cov80, ref,
      memberCount: rescaled.length,
    };
  }, [outbreakId, members, r0Spread, outbreaks]);

  if (outbreaks.length === 0) {
    return (
      <div className="surface">
        <div className="surface__header">
          <span className="surface__sec">§7.4c</span>
          <h1 className="surface__title">Model Validation</h1>
        </div>
        <div className="panel">
          <div style={{ padding: 'var(--bm-space-3)', fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
            No validation corpus loaded. The observed-outbreak series live in
            <code> @biomax/plugin-epi-validation/data/outbreaks/</code>; run
            <code> npm run fetch:corpus</code> in that package to populate them.
          </div>
        </div>
      </div>
    );
  }
  if (!view) return <div>Loading…</div>;
  if ('catalogMiss' in view) {
    return (
      <div className="surface">
        <div className="surface__header">
          <span className="surface__sec">§7.4c</span>
          <h1 className="surface__title">Model Validation</h1>
        </div>
        <div className="panel">
          <div style={{ padding: 'var(--bm-space-3)', fontSize: 13, color: 'var(--bm-text-muted)' }}>
            Corpus outbreak <strong>{view.o.id}</strong> references pathogen
            <code> {view.o.pathogenId}</code> / region <code>{view.o.regionId}</code>, which are not in the
            loaded catalog — cannot run the backtest.
          </div>
        </div>
      </div>
    );
  }

  const { o, raw, shape, ref } = view;

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.4c</span>
        <h1 className="surface__title">Model Validation · backtest vs observed</h1>
      </div>
      <div className="surface__sub">
        unmitigated SEIRD vs real historical case data · peak-timing + shape are the
        meaningful metrics (magnitude is caveated) · {view.memberCount}-member rescaled ensemble for calibration
      </div>

      {/* Provenance — this is REAL observed data, with a verified source. */}
      <div className="panel panel--accent" style={{ marginBottom: 'var(--bm-space-4)' }}>
        <div style={{ padding: 'var(--bm-space-3)', fontSize: 'var(--bm-text-xs)', color: 'var(--bm-text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--bm-accent)' }}>OBSERVED DATA — verified source.</strong>{' '}
          {o.label}: {o.series.length} weekly points ({o.window.from} → {o.window.to}) from{' '}
          <strong>{o.source.name}</strong>
          {o.source.pmid && <> · PMID <code>{o.source.pmid}</code></>}
          {' '}· retrieved {o.source.retrievedAt}.
          {ref && <> Citation round-tripped via <code>fetchByPmid</code> on {ref.verifiedAt}.</>}
          {' '}{o.note}
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Backtest configuration</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Outbreak</span>
            <select value={outbreakId} onChange={e => setOutbreakId(e.target.value)}>
              {outbreaks.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Ensemble members · {members}</span>
            <input type="range" min="10" max="60" step="1" value={members}
              onChange={e => setMembers(parseInt(e.target.value, 10))} />
          </label>
          <label className="simctl__field">
            <span className="simctl__label">R0 spread · ±{r0Spread}%</span>
            <input type="range" min="5" max="50" step="1" value={r0Spread}
              onChange={e => setR0Spread(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Peak-timing error</div>
          <div className="kpi__value kpi__value--signal">{raw.peakTimingErrorWeeks} wk</div>
          <div className="kpi__sub">model peak vs observed peak</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Shape RMSE</div>
          <div className="kpi__value kpi__value--accent">{shape.rmse.toFixed(3)}</div>
          <div className="kpi__sub">peak-normalised · 0 = identical</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Mean weekly CRPS</div>
          <div className="kpi__value">{Math.round(view.crpsMean).toLocaleString()}</div>
          <div className="kpi__sub">cases · rescaled ensemble</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Central-80% coverage</div>
          <div className="kpi__value">{(view.cov80 * 100).toFixed(0)}%</div>
          <div className="kpi__sub">ideal 80% · shape calibration</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Epidemic shape · observed vs model (peak-normalised)</span>
          <span className="panel__meta">isolates timing + shape from the magnitude gap</span>
        </div>
        <Sparkline values={view.obsNorm} label="observed (confirmed cases)" stroke="var(--bm-accent)" />
        <Sparkline values={view.modNorm} label="model (unmitigated SEIRD)" stroke="var(--bm-danger)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Observed vs rescaled ensemble · 95% predictive band</span>
          <span className="panel__meta">ensemble rescaled to observed final size — tests temporal shape coverage</span>
        </div>
        <Sparkline values={view.observed} band={{ low: view.q025, high: view.q975 }}
          label="observed (line) within ensemble 95% band" stroke="var(--bm-accent)" />
      </div>

      <div className="watch-grid">
        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">Reliability diagram</span>
            <span className="panel__meta">predicted quantile → empirical coverage</span>
          </div>
          <ReliabilityDiagram points={view.calibration} />
        </div>

        <div className="panel">
          <div className="panel__head"><span className="panel__title">Calibration table</span></div>
          <table className="dtable">
            <thead>
              <tr><th>Predicted q</th><th className="num">Empirical</th><th className="num">Δ</th></tr>
            </thead>
            <tbody>
              {view.calibration.map(c => {
                const d = c.empiricalCoverage - c.predictedQuantile;
                return (
                  <tr key={c.predictedQuantile}>
                    <td>{c.predictedQuantile.toFixed(2)}</td>
                    <td className="num">{c.empiricalCoverage.toFixed(2)}</td>
                    <td className="num" style={{ color: Math.abs(d) <= 0.1 ? 'var(--bm-accent)' : 'var(--bm-signal)' }}>
                      {d >= 0 ? '+' : ''}{d.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Magnitude error</span>
          <span className="panel__meta">caveated — see below</span>
        </div>
        <div style={{ padding: 'var(--bm-space-3)', fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          Peak-size error <strong>{(raw.peakSizeErrorFrac * 100).toFixed(0)}%</strong> ·
          final-size error <strong>{(raw.finalSizeErrorFrac * 100).toFixed(0)}%</strong>{' '}
          over {raw.weeks} scored weeks.
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">How to read this</span></div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          The model here is <strong>unmitigated</strong> and counts <strong>true infections</strong>;
          the observed series counts <strong>confirmed cases</strong> after real interventions (Italy
          locked down 2020-03-09). So a large positive magnitude error is <em>expected</em> — it
          quantifies the counterfactual the lockdown averted, not a model defect. The honest, scale-free
          measures of fit are <strong>peak-timing error</strong> ({raw.peakTimingErrorWeeks} wk) and
          peak-normalised <strong>shape RMSE</strong> ({shape.rmse.toFixed(3)}). The reliability diagram
          rescales the ensemble to the observed final size first, so it asks a fair question: does the
          model's <em>shape uncertainty</em> bracket the observed curve over time? Points on the diagonal
          = well-calibrated.
        </div>
      </div>
    </div>
  );
}

/** Minimal reliability diagram: predicted quantile (x) vs empirical coverage (y),
 *  with the y=x perfect-calibration diagonal. No deps. */
function ReliabilityDiagram({ points }: { points: readonly CalibrationPoint[] }) {
  const s = 280, pad = 34, plot = s - pad * 2;
  const x = (q: number) => pad + q * plot;
  const y = (c: number) => pad + plot - c * plot;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} style={{ width: '100%', maxWidth: s, display: 'block', margin: '0 auto' }}>
      {/* frame */}
      <rect x={pad} y={pad} width={plot} height={plot} fill="none" stroke="var(--bm-border)" strokeWidth="1" />
      {/* perfect-calibration diagonal */}
      <line x1={x(0)} y1={y(0)} x2={x(1)} y2={y(1)} stroke="var(--bm-text-subtle)" strokeWidth="1" strokeDasharray="4 4" />
      {/* calibration polyline */}
      <path d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.predictedQuantile)} ${y(p.empiricalCoverage)}`).join(' ')}
        fill="none" stroke="var(--bm-accent)" strokeWidth="1.8" strokeLinejoin="round" />
      {points.map(p => (
        <circle key={p.predictedQuantile} cx={x(p.predictedQuantile)} cy={y(p.empiricalCoverage)} r="3.5" fill="var(--bm-accent)" />
      ))}
      {/* axis labels */}
      <text x={pad + plot / 2} y={s - 6} textAnchor="middle" fontSize="10" fill="var(--bm-text-subtle)" fontFamily="var(--bm-font-mono)">predicted quantile</text>
      <text x={10} y={pad + plot / 2} textAnchor="middle" fontSize="10" fill="var(--bm-text-subtle)" fontFamily="var(--bm-font-mono)" transform={`rotate(-90 10 ${pad + plot / 2})`}>empirical coverage</text>
    </svg>
  );
}
